import {
  Table,
  useAlert,
  useModal,
  ModalTypeEnum,
  SearchBar,
  PlusIcon,
  SmallButton,
  MultipleOptionsDropdown,
} from "@wit/mpesa-ui-components";
import { ColumnProps, SortDirectionEnum } from "@wit/mpesa-ui-components/lib/components/table/table.component";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IStoreInterface } from "../../../../configs/store.config";
import { PageContainer, PageTitle, LoadingText } from "../../../../shared/shared.styled";
import styled from "styled-components";
import TablePagination from "../../../../shared/components/table-pagination/table-pagination-component";
import { RoutesEnum } from "../../../../routes/routes.constants";
import { useHistory, useLocation } from "react-router-dom";
import {
  SmallButtonsContainer,
  OtherFiltersContainer,
  SearchBarFilterContainer,
} from "../../../../shared/responsive-ui.styled";
import { getRoles } from "../../../../shared/shared.utils";
import { SharedDropdownOption } from "@wit/mpesa-ui-components/lib/components/dropdown/shared-dropdown-option-container/shared-dropdown-option-container.component";
import IdentityApi from "../identity.api";
import { IdentityActions } from "../identity.store";
import { ILoggedUser } from "../../../authentication/authentication.interfaces";
import { IUpdateIdentityResponse } from "../identity.interfaces";
import { IIdentityDocument } from "../../../../shared/models/identity.model";
import { IdentityDocUtils } from "../identity.utils";
import { CaptureBackSideEnums } from "../../ekyc.shared.enums";
import { normalizeErrorMessage } from "../../../../shared/utils/errorMessageHelper.utils";
import { cleanBackendMessage } from "../../../../shared/utils/cleanBackendErrorMessage.utils";

const ManageIdentityPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [showAlert, , setAlertProps] = useAlert();
  const [, , setConfirmModalProps] = useModal(ModalTypeEnum.ConfirmationModal);

  const [searchString, setSearchString] = useState("");
  const [rolesSelected, setRolesSelected] = useState<string[]>([]);
  const [activePage, setActivePage] = useState(1);
  const [sortColumnFormKey, setSortColumnFormKey] = useState("status");
  const [sortColumnDirection, setSortColumnDirection] = useState(SortDirectionEnum.ASC);
  const pageSize = 10;

  const { state } = useLocation<any>();
  const submissionSavedSuccessfully = Boolean(state?.successfulUpdate);

  // const user = useSelector((s: IStoreInterface) => s.authenticationReducer.loggedUser as ILoggedUser);
  const identityDocs = useSelector((s: IStoreInterface) => s.identitiesReducer.users || []);

  const [isFetchingDocs, setIsFetchingDocs] = useState(false);

  const mappedIdentityDocs: IUpdateIdentityResponse[] = identityDocs.map((doc: IIdentityDocument) => ({
    ...doc,
    captureBackSide: doc.captureBackSide === true ? CaptureBackSideEnums.TRUE : CaptureBackSideEnums.FALSE,
  }));

  //const paginatedIdentityDocs = mappedIdentityDocs.slice((activePage - 1) * pageSize, activePage * pageSize);
  const filteredDocs = mappedIdentityDocs.filter(doc => {
    const lowerSearch = searchString.toLowerCase();

    const matchesSearch = searchString
      ? (doc.documentId?.toLowerCase().includes(lowerSearch) ?? false) ||
        (doc.en?.toLowerCase().includes(lowerSearch) ?? false) ||
        (doc.fr?.toLowerCase().includes(lowerSearch) ?? false) ||
        (doc.documentType?.toLowerCase().includes(lowerSearch) ?? false)
      : true;

    const matchesRole = rolesSelected.length ? rolesSelected.includes(String(doc.status)) : true;

    return matchesSearch && matchesRole;
  });

  const paginatedIdentityDocs = filteredDocs.slice((activePage - 1) * pageSize, activePage * pageSize);

  useEffect(() => {
    setActivePage(1);
  }, [searchString, rolesSelected]);

  useEffect(() => {
    if (submissionSavedSuccessfully) {
      setAlertProps({
        title: t("pages.docSubmissionDetails.changeDetailsSuccess"),
        type: AlertTypeEnum.SUCCESS,
      });
      showAlert();
    }
  }, []);

  useEffect(() => {
    getDocuments();
  }, []);

  const getDocuments = () => {
    setIsFetchingDocs(true);
    dispatch(IdentityActions.creators.fetchingDocumentsAction());

    IdentityApi.methods
      .getDocuments()
      .then(res => {
        const identityDocuments: IIdentityDocument[] = Array.isArray(res.data.identityDocuments)
          ? res.data.identityDocuments
          : [];

        dispatch(IdentityActions.creators.fetchDocumentsSuccessAction(identityDocuments));
        setIsFetchingDocs(false);
      })
      .catch((error: any) => {
        let backendMessage = error?.data?.status?.message || t("pages.docValidation.getDocsError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      });
  };

  const addNewUser = () => {
    history.push(RoutesEnum.ADD_IDENTITY);
  };

  const handleSortChange = (sortColumn: ColumnProps, sortDirection: SortDirectionEnum) => {
    setSortColumnFormKey(sortColumn.formKey || "");
    setSortColumnDirection(sortDirection);
  };

  const handlePageChange = (pageNumber: number) => {
    setActivePage(pageNumber);
  };

  const isOptionSelected = (opt: SharedDropdownOption) => rolesSelected.includes(opt.key);
  const toggleOption = (opt: SharedDropdownOption) => {
    setRolesSelected(prev => (prev.includes(opt.key) ? prev.filter(k => k !== opt.key) : [...prev, opt.key]));
  };
  const resetDropdown = () => setRolesSelected([]);

  return (
    <PageContainer>
      <PageTitle style={{ marginBottom: 43 }}>{t("pages.manageIdentity.title")}</PageTitle>
      <DocValidationListContainer>
        <FiltersContainer>
          <SearchBarFilterContainer>
            <SearchBar
              placeholderLabel={t("pages.manageIdentity.searchBarPlaceholder")}
              value={searchString}
              onChange={e => setSearchString(e.target.value)}
              clearValue={() => setSearchString("")}
            />
          </SearchBarFilterContainer>
          <SmallButtonsContainer>
            <SmallButton
              onClick={addNewUser}
              titleLabel={t("pages.manageIdentity.addOperatorBtn")}
              iconComponent={
                <IconContainer>
                  <PlusIcon />
                </IconContainer>
              }
            />
          </SmallButtonsContainer>
        </FiltersContainer>

        {isFetchingDocs ? (
          <LoadingText>{t("commons.loadingResults")}</LoadingText>
        ) : (
          <>
            <Table<IUpdateIdentityResponse>
              columns={IdentityDocUtils.getTableColumns()}
              values={paginatedIdentityDocs}
              rowClickAction={(_, rowData) =>
                history.push({
                  pathname: RoutesEnum.IDENTITY_DOC_DETAILS,
                  state: { urn: rowData.urn },
                })
              }
            />

            {identityDocs.length > 0 && (
              <TablePagination
                handlePageChange={handlePageChange}
                totalItems={identityDocs.length}
                activePage={activePage}
                pageSize={pageSize}
              />
            )}
          </>
        )}
      </DocValidationListContainer>
    </PageContainer>
  );
};

export default ManageIdentityPage;

const DocValidationListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 50px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    gap: 24px;
  }
`;

const IconContainer = styled.div`
  stroke: ${props => props.theme.palette.vodafoneRed};
  width: 20px;
  height: 20px;

  svg {
    height: 20px;
  }
`;
